import { useEffect, useRef, useState } from 'react';

import {
    getWindowDimensions,
    needsConservativeMobileResumeMeasure,
    setBodyOverflow,
} from 'App/Helpers';

/**
 * Hook computes game canvas dimensions based on the device type.
 * App/hooks/games/docs/useGameCanvasDimensions.md
 *
 * Mobile: locks height at mount, re-measures on settle and when returning from background.
 * Desktop: one-time aspect-ratio sizing on mount, no resize/visibility listeners.
 *
 * @param isMobile - boolean
 *
 * @return object - { canvasWidth, canvasHeight, layoutRevision } — layoutRevision bumps once per resume cycle when height is unchanged (forces Unity resize).
 */
export const useGameCanvasDimensions = (isMobile) => {
    const [canvasDimensions, setCanvasDimensions] = useState({
        canvasWidth: '',
        canvasHeight: '',
    });
    const [layoutRevision, setLayoutRevision] = useState(0);
    const lastAppliedRef = useRef(null);
    const pageWasFrozenRef = useRef(false);

    useEffect(() => {
        const body = document.body;
        const previousInline = body.getAttribute('style') || '';

        const forceHidden = () => {
            body.style.setProperty('overflow', 'hidden', 'important');
        };

        if (body.style.getPropertyValue('overflow') === 'visible')
            forceHidden();

        const ob = new MutationObserver((muts) => {
            for (const m of muts) {
                if (m.type === 'attributes' && m.attributeName === 'style') {
                    if (body.style.getPropertyValue('overflow') === 'visible') {
                        forceHidden();
                    }
                }
            }
        });

        ob.observe(body, { attributes: true, attributeFilter: ['style'] });

        return () => {
            ob.disconnect();
            body.setAttribute('style', previousInline);
        };
    }, []);

    useEffect(() => {
        setBodyOverflow({ overflow: 'hidden' });

        if (!isMobile) {
            const { width, height } = getWindowDimensions();

            if (width >= height || width / height > 0.5625) {
                setCanvasDimensions({
                    canvasWidth: `${(height * 9) / 16}px`,
                    canvasHeight: '100%',
                });
            } else {
                setCanvasDimensions({
                    canvasWidth: '100%',
                    canvasHeight: `${(width * 16) / 9}px`,
                });
            }

            return;
        }

        let rafId = null;
        const pendingTimeouts = [];
        let detachViewportResize = null;

        const readHeightSamples = () => {
            window.scrollTo(0, 0);

            return [
                window.innerHeight,
                document.documentElement.clientHeight,
                window.visualViewport?.height,
            ].filter((value) => Number.isFinite(value));
        };

        const measureMobileHeight = ({ preferSmallest = false } = {}) => {
            const samples = readHeightSamples();

            if (!samples.length) {
                return Math.round(window.innerHeight);
            }

            if (preferSmallest && needsConservativeMobileResumeMeasure()) {
                return Math.round(Math.min(...samples));
            }

            const vvHeight = window.visualViewport?.height;
            if (Number.isFinite(vvHeight)) {
                return Math.round(vvHeight);
            }

            return Math.round(Math.max(...samples));
        };

        const buildMobileDimensions = (options) => ({
            canvasWidth: '100%',
            canvasHeight: `${measureMobileHeight(options)}px`,
        });

        const bumpLayoutRevision = () => {
            setLayoutRevision((revision) => revision + 1);
        };

        const applyDimensions = (next) => {
            const isUnchanged =
                lastAppliedRef.current?.canvasWidth === next.canvasWidth &&
                lastAppliedRef.current?.canvasHeight === next.canvasHeight;

            if (isUnchanged) {
                return false;
            }

            lastAppliedRef.current = next;
            setCanvasDimensions(next);
            return true;
        };

        const clearPendingSettle = () => {
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
                rafId = null;
            }

            while (pendingTimeouts.length) {
                window.clearTimeout(pendingTimeouts.pop());
            }

            detachViewportResize?.();
            detachViewportResize = null;
        };

        const queueDelayedMeasures = (runMeasure, delays) => {
            delays.forEach((delayMs) => {
                pendingTimeouts.push(
                    window.setTimeout(() => {
                        runMeasure();
                    }, delayMs)
                );
            });
        };

        const attachTemporaryViewportResizeListener = (
            runMeasure,
            durationMs
        ) => {
            const viewport = window.visualViewport;
            if (!viewport) return;

            const onViewportResize = () => {
                runMeasure();
            };

            viewport.addEventListener('resize', onViewportResize);

            pendingTimeouts.push(
                window.setTimeout(() => {
                    viewport.removeEventListener('resize', onViewportResize);
                }, durationMs)
            );

            detachViewportResize = () => {
                viewport.removeEventListener('resize', onViewportResize);
            };
        };

        const scheduleMountSettle = () => {
            clearPendingSettle();

            const runMeasure = () => {
                applyDimensions(
                    buildMobileDimensions({ preferSmallest: false })
                );
            };

            runMeasure();

            rafId = requestAnimationFrame(() => {
                rafId = null;
                runMeasure();
                queueDelayedMeasures(runMeasure, [250]);
            });
        };

        const RESUME_VIEWPORT_LISTEN_MS = 2000;
        const RESUME_FINISH_MS = 2000;

        const scheduleResumeSettle = () => {
            clearPendingSettle();

            const preferSmallest = needsConservativeMobileResumeMeasure();
            let dimensionsChangedDuringCycle = false;
            let resumeCycleFinished = false;

            const runMeasure = () => {
                const changed = applyDimensions(
                    buildMobileDimensions({ preferSmallest })
                );
                if (changed) dimensionsChangedDuringCycle = true;
            };

            const finishResumeCycle = () => {
                if (resumeCycleFinished) return;
                resumeCycleFinished = true;

                if (!dimensionsChangedDuringCycle) {
                    bumpLayoutRevision();
                }
            };

            runMeasure();

            rafId = requestAnimationFrame(() => {
                rafId = null;
                runMeasure();
                queueDelayedMeasures(runMeasure, [250]);

                pendingTimeouts.push(
                    window.setTimeout(() => {
                        runMeasure();
                    }, 1500)
                );

                attachTemporaryViewportResizeListener(
                    runMeasure,
                    RESUME_VIEWPORT_LISTEN_MS
                );

                pendingTimeouts.push(
                    window.setTimeout(() => {
                        runMeasure();
                        finishResumeCycle();
                    }, RESUME_FINISH_MS)
                );
            });
        };

        scheduleMountSettle();

        const onFreeze = () => {
            pageWasFrozenRef.current = true;
        };

        const onResume = () => {
            scheduleResumeSettle();
            pageWasFrozenRef.current = false;
        };

        const onVisibilityChange = () => {
            if (document.visibilityState !== 'visible') return;
            scheduleResumeSettle();
        };

        const onPageShow = (event) => {
            if (event.persisted || pageWasFrozenRef.current) {
                scheduleResumeSettle();
                pageWasFrozenRef.current = false;
            }
        };

        document.addEventListener('freeze', onFreeze);
        document.addEventListener('resume', onResume);
        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('pageshow', onPageShow);

        return () => {
            clearPendingSettle();
            document.removeEventListener('freeze', onFreeze);
            document.removeEventListener('resume', onResume);
            document.removeEventListener(
                'visibilitychange',
                onVisibilityChange
            );
            window.removeEventListener('pageshow', onPageShow);
        };
    }, []);

    return { ...canvasDimensions, layoutRevision };
};
